/**
 * Post-payment landing.
 *
 * Reads the order by its public token and shows status:
 *   paid       → "Your PDF is being prepared; check your email in ~60s"
 *   delivered  → "PDF was emailed to <masked email>"
 *   pending    → "We're still verifying — refresh in a moment"
 *
 * Polls the order every 5s for up to 2 minutes so the page can flip
 * from paid → delivered automatically.
 */
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useLang } from '@/i18n/useLang';
import { storefrontApi } from '@/api/storefront';
import { StorefrontHeader } from '@/components/storefront/StorefrontHeader';

export function ThankYouPage() {
  const { token } = useParams<{ token: string }>();
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const statusQ = useQuery({
    queryKey: ['storefront', 'order', token],
    queryFn: () => storefrontApi.orderStatus(token!),
    enabled: !!token,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 5_000;
      if (data.status === 'delivered') return false;
      return 5_000;
    },
  });

  useEffect(() => {
    // Stop polling after 2 minutes regardless
    if (!token) return;
    const tm = setTimeout(() => statusQ.refetch(), 120_000);
    return () => clearTimeout(tm);
  }, [token, statusQ]);

  if (!token) {
    return (
      <>
        <StorefrontHeader />
        <main className="mx-auto max-w-screen-md px-4 py-10 text-center text-sm text-stone-400">
          {t('आदेश-टोकन नहीं मिला।', 'No order token.')}
        </main>
      </>
    );
  }

  return (
    <>
      <StorefrontHeader />
      <main className="mx-auto max-w-screen-md px-4 py-10 lg:py-16">
        <div className="rounded-2xl bg-stone-900/60 p-6 text-center ring-1 ring-stone-800 lg:p-10">
          <div className="text-4xl">🙏</div>
          <h1 className="mt-3 text-2xl font-bold text-saffron-100">
            {t('धन्यवाद!', 'Thank you!')}
          </h1>

          {statusQ.isLoading && (
            <p className="mt-3 text-sm text-stone-400">
              {t('आदेश की स्थिति लोड हो रही है…', 'Loading order status…')}
            </p>
          )}

          {statusQ.data && (
            <>
              <p className="mt-3 text-sm leading-relaxed text-stone-300">
                {t(
                  `आपकी ${lang === 'hi' ? statusQ.data.report_title_hi : statusQ.data.report_title_en} रिपोर्ट तैयार की जा रही है।`,
                  `Your ${statusQ.data.report_title_en} report is being prepared.`,
                )}
              </p>
              <p className="mt-2 text-xs text-stone-500">
                {t(
                  `PDF ${statusQ.data.buyer_email_masked} पर भेजा जाएगा — आमतौर पर 60 सेकंड में।`,
                  `The PDF will arrive at ${statusQ.data.buyer_email_masked} — usually within 60 seconds.`,
                )}
              </p>

              <div className="mt-6 flex items-center justify-center gap-3">
                <StatusBadge status={statusQ.data.status} lang={lang} />
              </div>

              {statusQ.data.status === 'delivered' && (
                <p className="mt-4 text-sm text-emerald-300">
                  {t(
                    '✓ रिपोर्ट सफलतापूर्वक भेज दी गई है।',
                    '✓ Report successfully emailed.',
                  )}
                </p>
              )}

              <div className="mt-8 flex flex-col items-center gap-2 border-t border-stone-800 pt-6">
                <Link
                  to={`/order/${token}`}
                  className="text-xs text-saffron-300 hover:underline"
                >
                  {t('अपना आदेश ट्रैक करें / दोबारा भेजें →',
                     'Track your order / resend →')}
                </Link>
                <Link
                  to="/"
                  className="text-xs text-stone-400 hover:text-saffron-200"
                >
                  {t('और रिपोर्ट देखें', 'Browse more reports')}
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}

function StatusBadge({ status, lang }: { status: string; lang: 'hi' | 'en' }) {
  const labels: Record<string, { hi: string; en: string; tone: string }> = {
    pending:   { hi: 'भुगतान-प्रतीक्षित', en: 'Awaiting payment',   tone: 'bg-amber-900/40 text-amber-200 ring-amber-900' },
    paid:      { hi: 'भुगतान प्राप्त',    en: 'Payment received',   tone: 'bg-emerald-900/40 text-emerald-200 ring-emerald-900' },
    delivered: { hi: 'पहुँचा दिया गया',   en: 'Delivered',          tone: 'bg-emerald-900/40 text-emerald-200 ring-emerald-900' },
    failed:    { hi: 'विफल',              en: 'Failed',             tone: 'bg-rose-900/40 text-rose-200 ring-rose-900' },
    refunded:  { hi: 'धन-वापसी',          en: 'Refunded',           tone: 'bg-stone-800/40 text-stone-300 ring-stone-700' },
  };
  const meta = labels[status] ?? { hi: status, en: status, tone: 'bg-stone-800/40 text-stone-300 ring-stone-700' };
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${meta.tone}`}>
      {lang === 'hi' ? meta.hi : meta.en}
    </span>
  );
}
